/* eslint-disable react-refresh/only-export-components */
import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { AUTH } from 'src/constants/localStorageKeys';
import paths from 'src/navigation/paths';
import { apiBaseUrl } from 'src/utils/websocket';
import { User } from '../types/common';
import { ErrorHandler } from '../utils/errorHandler';

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  registrationSuccess: boolean;
  setRegistrationSuccess: (value: boolean) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  customerLoading: boolean;
  setCustomerLoading: (value: boolean) => void;
  ssoLoading: boolean;
  setSsoLoading: (value: boolean) => void;
  ssoSuccess: boolean;
  setSsoSuccess: (value: boolean) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  requestCompletedSuccess: boolean;
  setRequestCompletedSuccess: (value: boolean) => void;
  isOtpRequired: boolean;
  setIsOtpRequired: (value: boolean) => void;
  isSetUserAccountLoading: boolean;
  setIsSetUserAccountLoading: (value: boolean) => void;
  isVerificationRequestSuccess: boolean;
  setIsVerificationRequestSuccess: (value: boolean) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoSuccess, setSsoSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestCompletedSuccess, setRequestCompletedSuccess] = useState(false);
  const [isOtpRequired, setIsOtpRequired] = useState(false);
  const [isSetUserAccountLoading, setIsSetUserAccountLoading] = useState(false);
  const [isVerificationRequestSuccess, setIsVerificationRequestSuccess] =
    useState(false);

  useEffect(() => {
    const authToken = localStorage.getItem(AUTH);
    const isAuthPage = window.location.pathname.startsWith(paths.auth);
    const isCheckoutPage = window.location.pathname.startsWith(paths.checkout.replace(':paymentId', ''));

    if (authToken && !isAuthPage && !isCheckoutPage && !user?.id && !customerLoading) {
      const getCustomer = async () => {
        setCustomerLoading(true);

        try {
          const response = await fetch(`${apiBaseUrl}/api/merchant/me`, {
            headers: {
              'Content-Type': 'application/json',
              authorization: 'Bearer ' + authToken,
            },
          });

          const result = await response.json();

          if (!response.ok) {
            const errorMessage = result.message;

            ErrorHandler.handleError(errorMessage, { rethrow: true });
          }

          if (!result.is_onboarding_complete) {
            if (window.location.pathname !== paths.onboarding) {
              window.location.href = paths.onboarding;
            }
          } else if (!result.is_setup_complete) {
            if (window.location.pathname !== paths.sandbox) {
              window.location.href = paths.sandbox;
            }
          }

          setUser(() => ({ ...result }));
        } catch (error) {
          setUser(null);

          // Error initializing auth
          localStorage.removeItem(AUTH);

          window.location.href = paths.signIn;
        } finally {
          setCustomerLoading(false);
        }
      };

      getCustomer();
    }
  }, [user?.id, requestCompletedSuccess, customerLoading]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        registrationSuccess,
        setRegistrationSuccess,
        loading,
        setLoading,
        customerLoading,
        setCustomerLoading,
        ssoLoading,
        setSsoLoading,
        ssoSuccess,
        setSsoSuccess,
        isLoading,
        setIsLoading,
        requestCompletedSuccess,
        setRequestCompletedSuccess,
        isOtpRequired,
        setIsOtpRequired,
        isSetUserAccountLoading,
        setIsSetUserAccountLoading,
        isVerificationRequestSuccess,
        setIsVerificationRequestSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
